// pages/admin/LeadList.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import {
  MdSearch, MdChat, MdDelete, MdPerson, MdPhone, MdEmail,
  MdSchool, MdPublic,
} from 'react-icons/md';

export default function LeadList() {
  const base_url = import.meta.env.VITE_LOCAL_BASE_URL;

  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});

  const limit = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (search.trim()) params.search = search.trim();
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.college_type = typeFilter;

      const res = await axios.get(
        `${base_url}/api/v1/counselling-leads/admin/list`,
        { params }
      );
      if (res.data.success) {
        setLeads(res.data.data?.data || []);
        setPagination(
          res.data.data?.pagination || { total: 0, totalPages: 0 }
        );
      }
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || 'Failed');
    } finally {
      setLoading(false);
    }
  }, [base_url, page, search, statusFilter, typeFilter]);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (id, newStatus) => {
    setActionLoading((p) => ({ ...p, [id]: true }));
    const prev = leads;
    setLeads((p) =>
      p.map((l) => (l.id === id ? { ...l, status: newStatus } : l))
    );
    try {
      await axios.patch(
        `${base_url}/api/v1/counselling-leads/${id}/status`,
        { status: newStatus }
      );
    } catch (err) {
      setLeads(prev);
      alert(err?.response?.data?.message || 'Failed');
    } finally {
      setActionLoading((p) => ({ ...p, [id]: false }));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    setActionLoading((p) => ({ ...p, [id]: true }));
    try {
      await axios.delete(`${base_url}/api/v1/counselling-leads/${id}`);
      setLeads((p) => p.filter((l) => l.id !== id));
      setPagination((p) => ({ ...p, total: p.total - 1 }));
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed');
    } finally {
      setActionLoading((p) => ({ ...p, [id]: false }));
    }
  };

  const statusColor = (s) => {
    if (s === 'new') return 'primary';
    if (s === 'contacted') return 'warning';
    if (s === 'closed') return 'success';
    return 'secondary';
  };

  return (
    <div className="page-wrapper">
      <div className="container-fluid mt-3">
        <div className="card shadow border-0">
          <div className="card-header bg-white">
            <h4 className="fw-bold mb-3">
              <MdChat className="me-2 text-primary" />
              Counselling Leads
              <span className="badge bg-primary ms-2">
                {pagination.total}
              </span>
            </h4>

            <div className="row g-3">
              <div className="col-md-5">
                <div className="input-group">
                  <span className="input-group-text"><MdSearch /></span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search name, phone, email, college..."
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  />
                </div>
              </div>

              <div className="col-md-3">
                <select
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                >
                  <option value="">All Statuses</option>
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="closed">Closed</option>
                </select>
              </div>

              <div className="col-md-4">
                <select
                  className="form-select"
                  value={typeFilter}
                  onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
                >
                  <option value="">All College Types</option>
                  <option value="national">Indian Colleges</option>
                  <option value="international">International Colleges</option>
                </select>
              </div>
            </div>
          </div>

          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-bordered table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 60 }}>#</th>
                    <th>Student</th>
                    <th>NEET</th>
                    <th>College / Source</th>
                    <th style={{ width: 130 }}>Status</th>
                    <th>Date</th>
                    <th style={{ width: 120 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    [...Array(6)].map((_, i) => (
                      <tr key={i}>
                        {[...Array(7)].map((__, j) => (
                          <td key={j}><Skeleton /></td>
                        ))}
                      </tr>
                    ))
                  ) : leads.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center py-5 text-muted">
                        <MdChat size={40} />
                        <div className="mt-2">No leads yet</div>
                      </td>
                    </tr>
                  ) : (
                    leads.map((l, idx) => (
                      <tr key={l.id}>
                        <td>{(page - 1) * limit + idx + 1}</td>

                        <td>
                          <strong>{l.name}</strong>
                          <div className="small text-muted">
                            <MdPhone /> {l.phone}
                          </div>
                          {l.email && (
                            <div className="small text-muted">
                              <MdEmail /> {l.email}
                            </div>
                          )}
                        </td>

                        <td>
                          {l.neet_rank && (
                            <div className="small">
                              <strong>Rank:</strong> {l.neet_rank}
                            </div>
                          )}
                          {l.neet_score && (
                            <div className="small">
                              <strong>Score:</strong> {l.neet_score}
                            </div>
                          )}
                          {l.course && (
                            <div className="small text-muted">{l.course}</div>
                          )}
                        </td>

                        <td>
                          {l.college_name ? (
                            <div
                              className="d-flex align-items-start gap-1"
                              style={{ maxWidth: 260 }}
                            >
                              {l.college_type === 'international' ? (
                                <MdPublic
                                  className="text-info flex-shrink-0"
                                  style={{ marginTop: 3 }}
                                />
                              ) : (
                                <MdSchool
                                  className="text-primary flex-shrink-0"
                                  style={{ marginTop: 3 }}
                                />
                              )}
                              <div>
                                <strong>{l.college_name}</strong>
                                {l.college_type && (
                                  <div className="small text-muted text-capitalize">
                                    {l.college_type}
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="badge bg-light text-dark">
                              {l.source || 'general'}
                            </span>
                          )}
                        </td>

                        <td>
                          <select
                            className={`form-select form-select-sm text-${statusColor(
                              l.status
                            )}`}
                            value={l.status || 'new'}
                            disabled={actionLoading[l.id]}
                            onChange={(e) =>
                              handleStatusChange(l.id, e.target.value)
                            }
                          >
                            <option value="new">New</option>
                            <option value="contacted">Contacted</option>
                            <option value="closed">Closed</option>
                          </select>
                        </td>

                        <td>
                          {l.created_at
                            ? new Date(l.created_at).toLocaleString('en-IN', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : '-'}
                        </td>

                        <td>
                          <button
                            className="btn btn-sm btn-danger"
                            disabled={actionLoading[l.id]}
                            onClick={() => handleDelete(l.id)}
                          >
                            <MdDelete />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="text-muted">
                  Showing {Math.min((page - 1) * limit + 1, pagination.total)} -{' '}
                  {Math.min(page * limit, pagination.total)} of {pagination.total}
                </div>
                <div className="d-flex gap-2 align-items-center">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                  >
                    Previous
                  </button>
                  <span>
                    Page <strong>{page}</strong> of{' '}
                    <strong>{pagination.totalPages}</strong>
                  </span>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    disabled={page === pagination.totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}